import React, { useMemo, useState, useRef } from "react";
import "./help-user.css";

const helpTopics = [
  {
    id: 1,
    icon: "bi bi-camera-video",
    title: "How to Add Camera",
    content: "1. Open Camera Settings.\n2. Click Add New Camera.\n3. Scan the ip or either put rtp manually.\n4. Select Camera brand.\n5. ESelect IP.\n6. Enter Username & Password.\n7. Click Test Connection.\n8. Wait for Preview.\n9. Click Next.\n\nTips:\n✔ Camera and Promini must be reachable.\n✔ Verify RTSP URL.\n✔ Give meaningful camera names.",
  },
  {
    id: 2,
    icon: "bi bi-bell",
    title: "How to Add Notifications",
    content: `CamSec AI supports multiple types of notifications.

1. Suspect Notifications
• Suspect notifications are enabled by default.

2. Student / Staff / Member / Customer Notifications
• Go to Face Details.
• Click Edit Face Details for the person.
• On the Edit Face Details page, locate the "Notify <Name>" checkbox.
• Check the box to enable notifications for that person, or uncheck it to disable notifications.
• Save the changes.

3. Unauthorized Access & System Alert Notifications
• Go to Settings → Alerts.
• Select one or more alert types:
  - Unauthorized Access (Unknown Person)
  - Device Offline
  - Camera Offline
• Enable Push Notification if you want to receive notifications on your mobile device.
• Click Save to apply the changes.`,
  },
  {
    id: 3,
    icon: "bi bi-check-circle",
    title: "How to Activate Notifications",
    content: `To receive notifications on Telegram:

1. In the CamSec AI portal, go to Settings → Integrations and click on "Telegram Integration".

2. Click "Launch Telegram Bot" to open the bot in the Telegram app, and tap "Start" to instantly receive your Secure Chat ID.

3. Copy the Chat ID, paste it into the setup window, enter a Device Name (e.g., "My Phone"), and click "Save & Verify Connection". (You can link multiple devices by repeating this process).[Only 2 Chat_ids allowed]

4. Go to Settings → Alerts to enable system-wide notifications:
   • Unauthorized Access (Unknown Person)
   • Device Offline
   • Camera Offline

5. For individual Student, Staff, Member, or Customer notifications:
   • Go to Face Details.
   • Click Edit Face Details for the required person.
   • Enable the notification checkbox.
   • Save the changes. You will now receive the selected notifications across all connected Telegram devices.`,
  },
  {
    id: 4,
    icon: "bi bi-folder2-open",
    title: "Folder Structure",
    content: "Recommended Structure:\n• Cameras\n• Registered Faces\n• Unknown Faces\n• Attendance Reports\n• Logs\n• Backups\n• Configuration",
  },
  {
    id: 5,
    icon: "bi bi-tools",
    title: "Troubleshooting",
    content: `Camera Offline:
• Check if the camera is reachable by pinging its IP address.
  Example:
  ping 192.168.1.3
• If you receive replies such as "64 bytes from...", the camera is online.
• If there is no reply or the request times out, check the camera's network connection, router, or contact your camera vendor.
• If the camera responds to ping, verify that the camera password has not been changed.
• Go to Camera Settings → Edit Camera, select the camera, update the credentials if required, and save.

Face Not Recognized:
• Ensure the face is clearly visible with good lighting.
• Camera installation should follow these recommended specifications:
  - Mounting Height: 2.5–3 meters
  - Camera Tilt: 15°–30°
  - Resolution: Minimum 137 PPF
  - Detection Distance: Around 4 meters
  - Lighting: Minimum 200 lux with WDR enabled
• Make sure the camera lens is clean and the person's face is not obstructed.
• Re-register the face if recognition continues to fail.

System Offline:
• Check if the CamSec AI system is reachable by pinging its IP address.
• If the system is not reachable, restart the device, or try to open link on private/incognito browser
• If the issue persists after restarting, contact support@camsecai.com using your registered email address.`,
  },
  {
    id: 6,
    icon: "bi bi-camera",
    title: "Multiple Cameras",
    content: "2 Camera License: Supports up to 2 Cameras.\n4 Camera License: Supports up to 4 Cameras.\nNeed More? Contact CamSecAI Support.",
  },
  {
    id: 7,
    icon: "bi bi-webcam",
    title: "Supported Camera Brands",
    content: `CamSec AI supports all standard IP Cameras and DVR/NVR systems manufactured in or after 2022 with RTSP enabled.

Supported brands include:
✔ Hikvision
✔ Dahua
✔ CP Plus
✔ Axis
✔ Uniview
✔ TP-Link (VIGI)
✔ Hanwha Vision
✔ Bosch
✔ Any ONVIF/RTSP-compatible IP Camera
✔ DVR/NVR systems with RTSP enabled`,
  },
  {
    id: 8,
    icon: "bi bi-person-bounding-box",
    title: "Why Face Recognition?",
    content: "✔ Contactless Attendance\n✔ High Accuracy\n✔ Real-Time Monitoring\n✔ Duplicate Prevention\n✔ Centralized Records",
  },
  {
    id: 9,
    icon: "bi bi-brightness-high",
    title: "Lighting Requirements",
    content: "Recommended: White LED, Natural Light.\nAvoid: Darkness, Backlight, Flash.",
  },
  {
    id: 10,
    icon: "bi bi-globe",
    title: "Portal Not Opening",
    content: `If the CamSec AI portal is not opening, follow these steps:

1. Verify the System is Online
• Check that the CamSec AI device is powered on.
• If yes, please try to open it ion browser.
• Check that the Cable you connect[LAN] is having internet by pinging 8.8.8.8.
• If the request times out, restart the system and ensure it is connected to the network.

2. Check Your Internet & Network
• Make sure your computer is connected to the same network as the CamSec AI system (for local access).
• If accessing remotely, verify that your internet connection is working.

3. Verify the Portal Address
• Ensure you are opening the correct IP address or domain.
• Double-check the port number if one is required.

4. Try Another Browser
• Open the portal in Chrome, Edge, or Firefox.
• Clear the browser cache or try Incognito/Private Mode.

5. Check Firewall & Antivirus
• Ensure your firewall or antivirus is not blocking access to the portal.
• If required, temporarily disable it for testing.

6. Restart the System
• Restart the CamSec AI system and wait 2–3 minutes for all services to start.
• Try opening the portal again.

7. Still Not Working?
• If the system responds to ping but the portal still does not open, or the domain remains inaccessible after restarting, contact support@camsecai.com using your registered email address. Include your system serial number, portal URL/IP, and a screenshot of the issue for faster assistance.`,
  },
  {
    id: 11, // Fixed ID (was duplicate 8)
    icon: "bi bi-diagram-3",
    title: "Integrations",
    content: `CamSec AI supports integration with:
✔ HRMS
✔ ERP
✔ Payroll Systems
✔ Visitor Management Systems
✔ School ERP
✔ REST APIs

To request an integration:
• Go to Settings → Integrations.
• Select the integration you require.
• Fill in the requested details and submit the form.

Our team will review your request and send the complete API documentation, integration guide, and endpoint details to your registered email address.`,
  },
  {
    id: 12,
    icon: "bi bi-person-badge",
    title: "Why Multiple Photos?",
    content: `CamSec AI requires multiple photos of each person during registration to achieve the highest recognition accuracy.

Capture photos from these angles:
• Front
• Slight Left
• Slight Right
• Slight Up
• Slight Down

These images help the AI learn the person's facial features from different viewpoints, making recognition reliable even if the person is not looking directly at the camera.

For the best results:
• Ensure good lighting with no harsh shadows.
• Keep a neutral expression.
• Avoid sunglasses, face masks, or anything covering the face.
• Maintain a clear, focused image with the entire face visible.

Providing multiple angles significantly improves recognition accuracy and reduces missed detections in real-world scenarios.`,
  },
  {
    id: 13,
    icon: "bi bi-exclamation-triangle",
    title: "Add Suspects From CCTV",
    content: `If a person is considered a potential security threat, you can register them as a suspect using images captured from your CCTV recordings. Once registered, CamSec AI will continuously monitor all connected cameras and immediately notify you if the person is detected again.

To add a suspect:
• Open Face Details.
• Click Add Suspect.
• Enter the person's details.
• If the person's name is unknown, you can use a temporary name such as "Suspect 1", "Suspect 2", etc.
• Upload multiple clear face images extracted from CCTV footage, or capture live photos if the person is present.
• Include photos from different angles (Front, Slight Left, Slight Right, Slight Up, Slight Down) whenever possible.
• Click Save.

After registration, CamSec AI will automatically monitor all connected cameras. If the suspect appears again, an instant notification will be sent through the configured notification channel (Telegram).`,
  },
  {
    id: 14,
    icon: "bi bi-people",
    title: "Administrator & User Roles",
    content: `CamSec AI provides role-based access control with two primary user roles to ensure security and controlled access.

1. IT Administrator
The IT Administrator has complete access to the system and can:
• Add, edit and delete users
• Register and manage faces
• Add Students, Staff, Members, Customers and Suspects
• Configure cameras and RTSP settings
• Manage notifications and alerts
• Configure integrations and system settings
• Access all reports, analytics and dashboards
• Manage user permissions and system configuration

2. User / Management
The User/Management role is designed for principals, managers, HR teams, security personnel and authorized staff. They can:
• View dashboards and analytics
• Access attendance reports
• View footfall analytics
• Download attendance reports for Students, Staff, Members and Customers
• View entry & exit logs
• Receive notifications and alerts if enabled by the IT Administrator

Users in this role cannot modify system settings, camera configurations, registered faces, integrations or administrative configurations.`,
  },
  {
    id: 15,
    icon: "bi bi-robot",
    title: "What is CamSecAI Pro Mini?",
    content: `CamSecAI Pro Mini is an on-premise AI-powered appliance that transforms your existing CCTV cameras into an intelligent security, attendance, and monitoring system. It performs all AI processing locally, providing real-time recognition, instant alerts, and enterprise-grade privacy without requiring cloud processing.

Key Features:
• Face Recognition Attendance
• Student Attendance with Live Photo Notifications to Parents
• Employee Attendance & AI-Powered HRMS
• VVIP, VIP & Suspect Recognition
• Loyal & Regular Customer Recognition for Retail Stores
• Unknown Person & Unauthorized Access Detection
• Real-Time Telegram Notifications
• Visitor Management
• Camera Health Monitoring
• Attendance Reports & Analytics
• HRMS, ERP & REST API Integrations
• Works with Existing IP Cameras

Industry Use Cases:

Schools & Colleges
• Automatic student attendance
• Sends live attendance photos to respective parents
• Staff attendance
• Unauthorized person detection
• Parent notifications and attendance reports

Corporate Offices
• AI-powered HRMS with automatic employee attendance
• Entry & Exit tracking
• Visitor management
• Department-wise attendance reports
• Shift and payroll integrations

Retail Stores & Malls
• Recognizes VVIPs, VIPs, loyal customers, and regular customers
• Detects suspects and blacklisted individuals
• Sends instant Telegram alerts when important customers or suspects arrive
• Provides customer visit analytics and repeat visitor insights

Hospitals, Hostels & Residential Societies
• Staff and resident attendance
• Visitor monitoring
• Unauthorized access alerts
• Security and safety monitoring

Learn more:
https://promini.camsecai.com`,
  },
  {
    id: 16,
    icon: "bi bi-bar-chart-line",
    title: "Attendance",
    content: `CamSec AI provides fully automatic AI-powered attendance using face recognition, eliminating the need for biometric devices, RFID cards, or manual registers.

Features:
• Automatic attendance from existing CCTV cameras
• Real-time Entry & Exit tracking
• Student, Staff, Employee, Member & Visitor attendance
• Attendance with live photo verification
• Daily, Weekly and Monthly attendance reports
• Export reports in Excel and PDF formats
• Late arrivals, early exits and absentee reports
• Department, Class and Batch-wise filtering
• Attendance analytics and dashboards
• Seamless integration with HRMS, ERP and Payroll systems.`,
  },
  {
    id: 17,
    icon: "bi bi-lightning",
    title: "Integration Request",
    content: `CamSec AI can be integrated with your existing software through secure REST APIs.

To request an integration:
• Open Settings → Integrations.
• Select the integration type (HRMS, ERP, Payroll, School ERP, Visitor Management or Custom API).
• Fill in the required details and submit the request.

Our integration team will review your request and email the complete API documentation, endpoint details, authentication method, sample requests, and implementation guide to your registered email address. Our technical team also provides deployment assistance whenever required.`,
  },
  {
    id: 18,
    icon: "bi bi-building",
    title: "On-site Support",
    content: `CamSec AI offers both remote and on-site deployment support depending on your service plan and location.

Support includes:
• Camera installation guidance
• Camera angle and positioning optimization
• AI system installation
• Network and RTSP configuration
• User training
• Integration assistance
• Performance optimization
• Health check and maintenance

Remote support is available for all customers. On-site support is provided based on your selected service plan and serviceable location. Please contact the CamSec AI Support Team for availability.Pricing is 2000 INR per site visit for india. For overseas, please contact support@camsecai.com`,
  },
  {
    id: 19,
    icon: "bi bi-hdd-network",
    title: "Data Storage",
    content: `CamSec AI is designed with privacy as a priority.

• All attendance records, face data, analytics, reports and settings are stored locally on your CamSec AI system.
• No biometric data or attendance records are stored on CamSec AI cloud servers.
• The web portal is securely connected to your local system through your dedicated CamSec AI subdomain, allowing authorized remote access without storing your data in the cloud.
• Your organization maintains complete ownership and control of all stored data.
• Data can be backed up locally according to your organization's policies.`,
  },
  {
    id: 20,
    icon: "bi bi-device-hdd",
    title: "Increase Storage",
    content: `If additional storage is required, CamSec AI can be expanded without affecting your existing data.

Supported storage options:
• Upgrade the internal SSD/HDD
• Add additional Hard Drives
• Connect a NAS (Network Attached Storage)
• Configure external storage devices for backups
• Archive older attendance records and reports to free up space

Since all data is stored locally, storage expansion remains under your organization's control. If you require assistance with storage planning or upgrades, please contact the CamSec AI Support Team.`,
  },

  /* --- NEWLY ADDED TOPICS START HERE --- */
  {
    id: 21,
    icon: "bi bi-cpu",
    title: "Model Configuration & Solutions",
    content: `⚠️ IMPORTANT: Model Configuration determines who the camera will recognize.

1. Go to "Area Select" on the left-side menu.
2. Select the Camera you want to configure.
3. Select the appropriate Solution based on your needs:

• STAFF ENTRY / STAFF EXIT:
  Configure this ONLY for Staff. 
  (Note: This will NOT process Members, Students, or Customers).

• MEMBER ENTRY / MEMBER EXIT:
  Configure this ONLY for Members, Students, or Customers. 
  (Note: This will NOT process Staff).

• UNIFIED ENTRY / UNIFIED STAFF:
  Use this if you have a single gate. This configuration processes everyone (Staff, Members, Students, and Customers) through a single entry point.`,
  },
  {
    id: 22,
    icon: "bi bi-bounding-box-circles",
    title: "Area Selection & Region Mapping",
    content: `Define the detection zones and active hours for your configured model.

1. Region Box Setup
• FULL: If you select "Full", the system will recognize faces across the entire camera frame.
• REGION: If you select "Region", you can mark a specific area by clicking points to draw a polygon. 
  (Example: To draw a 5-point area A-B-C-D-E, you must click point 'A' again at the end to close the shape).

2. Start Line & End Line
• Set the Start Line and End Line.
• If a person comes from the start line and goes to the end line and remains unidentified, the system throws an "Unknown" alert. If recognized, it saves the person.
• (Note: You can forget about the direction).

3. Time Schedule
• Set the Start Time and End Time for the solution.
• For a full 24-hour run: set Start Time to 00:00 (or 12:00) and End Time to 23:59 (or 11:59 PM).

4. Add Multiple Solutions
• You can modify or add multiple solutions to a single camera by clicking the green [+] button.

5. Save
• Click "Submit" to save your configuration.`,
  },
  {
    id: 23,
    icon: "bi bi-database-gear",
    title: "HRMS & ERP Integration Setup",
    content: `Connect CamSec AI directly to your existing HRMS or ERP platform for automated attendance syncing.

1. Go to Settings → Integrations.
2. Select your Integration Method (REST API, Webhook, or Direct Database Sync).
3. Enter the Endpoint URL and Authentication Tokens provided by your HRMS/ERP software.
4. Ensure the 'Employee ID' or 'Member ID' assigned in CamSec AI matches the ID in your ERP exactly.
5. Choose the Sync Frequency (Real-time upon detection, or Daily Batch sync).
6. Click 'Test Connection' to verify the data payload is received successfully.
7. Click Save.

For custom deployment scenarios or advanced API documentation, please request support via the Integrations page.`,
  },
  {
    id: 24,
    icon: "bi bi-cloud-arrow-down",
    title: "System Updates (OTA)",
    content: `Keep your CamSec AI system up to date with the latest AI Vision models, features, and security patches.

1. Go to System Settings → Updates.
2. Click 'Check for Updates'.
3. The system will securely connect to the update server and verify the versions of your Backend API, AI Model, Telegram Bot, and Database Schemas.
4. If new versions are found, the available components will be listed on the screen.
5. Click 'Install Update'.
6. The OTA system will smartly download only the modified files and automatically restart the necessary background services.
7. Do not disconnect the power or network during the update cycle. You will see a 'System Update Completed' message once finished.`,
  }
  /* --- NEWLY ADDED TOPICS END HERE --- */
];

const HelpTopicCard = ({ item, isOpen, onToggle, forwardRef }) => (
  <div ref={forwardRef} className={`premium-faq-card ${isOpen ? "is-open" : ""}`}>
    <button className="premium-faq-header" onClick={onToggle}>
      <div className="premium-faq-title-group">
        <div className="premium-icon-wrapper">
          <i className={item.icon}></i>
        </div>
        <span className="premium-faq-title">{item.title}</span>
      </div>
      <div className="premium-faq-chevron">
        <i className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
      </div>
    </button>
    
    <div className="premium-faq-body" style={{ height: isOpen ? "auto" : "0", opacity: isOpen ? "1" : "0" }}>
      <div className="premium-faq-content">
        {item.content}
      </div>
    </div>
  </div>
);

export default function HelpCenter() {
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(1);
  const topicRefs = useRef({});

  const filteredTopics = useMemo(() => {
    const query = search.toLowerCase();
    return helpTopics.filter(
      (item) =>
        item.title.toLowerCase().includes(query) ||
        item.content.toLowerCase().includes(query)
    );
  }, [search]);

  const handleScrollToTopic = (id) => {
    setOpenId(id);
    const element = topicRefs.current[id];
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <div className="premium-help-layout">
      {/* SIDEBAR NAVIGATION */}
      <aside className="premium-sidebar">
        <div className="premium-sidebar-header">
          <h2>Help Center</h2>
          <p>Find answers quickly</p>
        </div>
        
        <div className="premium-search-container">
          <i style={{marginTop : "-2%"}} className="bi bi-search search-icon"></i>
          <input
            type="text"
            className="premium-search-input"
            placeholder="Search guides..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="premium-nav-list">
          {filteredTopics.map((item) => (
            <button
              key={item.id}
              className={`premium-nav-item ${openId === item.id ? "active" : ""}`}
              onClick={() => handleScrollToTopic(item.id)}
            >
              <i className={item.icon}></i>
              <span className="nav-text">{item.title}</span>
            </button>
          ))}
          {filteredTopics.length === 0 && (
            <div className="premium-no-results">No matches found</div>
          )}
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="premium-main-content">
        
        {/* HERO BANNER */}
        <div className="premium-hero-banner">
          <div className="hero-content">
            <h1>CamSecAI Promini Support</h1>
            <p>Everything you need to master Camera Management, Face Recognition, Alerts, and Integration.</p>
          </div>
          <div className="hero-decoration"></div>
        </div>

        {/* QUICK LINK GRID */}
        <div className="premium-quick-grid">
          <div className="quick-card">
            <div className="quick-card-icon blue"><i className="bi bi-camera-video"></i></div>
            <h3>Camera Setup</h3>
            <p>Connect RTSP Cameras fast.</p>
          </div>
          <div className="quick-card">
            <div className="quick-card-icon purple"><i className="bi bi-robot"></i></div>
            <h3>AI Recognition</h3>
            <p>High Accuracy detection.</p>
          </div>
          <div className="quick-card">
            <div className="quick-card-icon orange"><i className="bi bi-bell"></i></div>
            <h3>Notifications</h3>
            <p>Real-time alerts setup.</p>
          </div>
          <div className="quick-card">
            <div className="quick-card-icon green"><i className="bi bi-bar-chart"></i></div>
            <h3>Reports</h3>
            <p>Attendance & Analytics.</p>
          </div>
        </div>

        {/* FAQ ACCORDION SECTION */}
        <div className="premium-guides-section">
          <h2 className="section-title">Detailed Guides</h2>
          <div className="premium-faq-list">
            {filteredTopics.map((item) => (
              <HelpTopicCard
                key={item.id}
                item={item}
                isOpen={openId === item.id}
                onToggle={() => setOpenId(openId === item.id ? null : item.id)}
                forwardRef={(el) => (topicRefs.current[item.id] = el)}
              />
            ))}
          </div>
        </div>

        {/* SUPPORT FOOTER */}
        <div className="premium-support-footer">
          <div className="support-info">
            <h2>Still need help?</h2>
            <p>Our dedicated support team is available Monday - Saturday.</p>
          </div>
          <div className="support-actions">
            <a href="mailto:support@camsecai.com" className="support-btn primary">
              <i className="bi bi-envelope"></i> support@camsecai.com
            </a>
            <a href="https://promini.camsecai.com/helpcenter" target="_blank" rel="noreferrer" className="support-btn secondary">
              <i className="bi bi-globe"></i> Visit Website
            </a>
          </div>
        </div>

      </main>
    </div>
  );
}
